<template>
  <div class="member-center">
    <PageTitle
      icon="chevron_left"
      title="會員中心設定"
      hideBtn
      style="padding-bottom: 24px"
      @iconClick="$router.push({ name: 'Parameters' })"
    />

    <!-- 會員中心連結 -->
    <MemberCenterLink v-if="useLiff" data-permit="'admin.lineLiff.page'" :liff="liff" @refresh="getLiff" />
    <!-- 客服連結 -->
    <ContactUsLink @update="refresh" />

    <MemberCenterColorTheme />

    <!-- 會員卡顯示設定 -->
    <MemberCardShow
      v-if="functionMenu"
      :configData="functionMenuShowConfig"
      @refresh="refresh"
    />

    <!-- 會員中心 QRcode 設定 -->
    <MemberCenterQRCodeSetting v-if="useMemberQRCode" />

    <MemberCardFuncShow :functionMenuConfig="functionMenuShowConfig" @refresh="refresh" />

    <section class="area-wrapper section-block">
      <SectionTitle title="底部快捷功能設定" @edit="showNavSettingDialog = true" />

      <BaseElForm label-position="left" label-width="65px">
        <BaseElFormItem label="排序1">
          <p>會員中心</p>
        </BaseElFormItem>
        <BaseElFormItem label="排序2">
          <p :class="{ 'gray-text': !formData.nav2.type }">
            {{ navTrans(formData.nav2.type) }}
          </p>
          <p>{{ formData.nav2.name }}</p>
          <p>{{ navUrl(formData.nav2) }}</p>
        </BaseElFormItem>
        <BaseElFormItem label="排序3">
          <p :class="{ 'gray-text': !formData.nav3.type }">
            {{ navTrans(formData.nav3.type) }}
          </p>
          <p>{{ formData.nav3.name }}</p>
          <p>{{ navUrl(formData.nav3) }}</p>
        </BaseElFormItem>
        <BaseElFormItem label="排序4">
          <p :class="{ 'gray-text': !formData.nav4.type }">
            {{ navTrans(formData.nav4.type) }}
          </p>
          <p>{{ formData.nav4.name }}</p>
          <p>{{ navUrl(formData.nav4) }}</p>
        </BaseElFormItem>
        <BaseElFormItem label="排序5">
          <p :class="{ 'gray-text': !formData.nav5.type }">
            {{ navTrans(formData.nav5.type) }}
          </p>
          <p>{{ formData.nav5.name }}</p>
          <p>{{ navUrl(formData.nav5) }}</p>
        </BaseElFormItem>
      </BaseElForm>
    </section>

    <!-- GA 串接設定 -->
    <MemberCenterGASetting />

    <!-- Dialog -->

    <el-dialog
      title="提示"
      :visible.sync="navItemWarningDialog"
      width="590px"
      :close-on-click-modal="false"
    >
      <p>請至「公司資訊設定」設定聯繫客服方式。</p>
      <div slot="footer">
        <BaseElButton type="primary">確認</BaseElButton>
      </div>
    </el-dialog>

    <el-dialog
      title="提示"
      :visible.sync="updateWarningDialog"
      width="590px"
      :close-on-click-modal="false"
    >
      <p>請至少選擇兩項快捷功能。</p>
      <div slot="footer">
        <BaseElButton type="primary">確認</BaseElButton>
      </div>
    </el-dialog>

    <NavFooterSettingDialog
      v-if="showNavSettingDialog"
      :navData="formData"
      :customerLink="contactCustomerLink"
      :allowNavFooter="allowNavFooterComputed"
      @close="showNavSettingDialog = false"
      @save="updateNavFooters"
    />
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { useShop } from '@/use/shop'
import { filter, forEach, find } from 'lodash'

import PageTitle from '@/components/Title/PageTitle.vue'
import MemberCenterLink from './components/MemberCenterLink.vue'
import MemberCardShow from './MemberCardShow/MemberCardShow.vue'
import ContactUsLink from './components/ContactUsLink.vue'
import MemberCardFuncShow from './components/MemberCardFuncShow.vue'
import MemberCenterColorTheme from './components/MemberCenterColorTheme.vue'
import MemberCenterGASetting from './components/MemberCenterGASetting.vue'
import MemberCenterQRCodeSetting from './components/MemberCenterQRCodeSetting.vue'
import NavFooterSettingDialog from '@/views/Parameters/MemberCenter/components/NavFooterSettingDialog.vue'
import SectionTitle from '@/components/Title/SectionTitle.vue'

import { navConfig } from '@/config/memberCenter'
import {
  GetMemberCenterConfig,
  UpdateMemberCenterConfig,
  GetShopInfo,
  GetAllowFooterNav,
} from '@/api/member/memberCenter'
import { GetLiff } from '@/api/shop'
import { useExternalWallet } from '@/use/useExternalWallet'
import { usePermissions } from '@/use/permissions'

export default defineComponent({
  name: 'MemberCenterSetting',
  components: {
    PageTitle,
    SectionTitle,
    NavFooterSettingDialog,
    MemberCenterLink,
    MemberCardShow,
    MemberCardFuncShow,
    ContactUsLink,
    MemberCenterColorTheme,
    MemberCenterGASetting,
    MemberCenterQRCodeSetting,
  },
  setup () {
    const { shopId } = useShop()
    const { checkAction, checkActionList } = usePermissions()
    const { externalWalletName, getExternalWalletConfig, enableExternalWallet } = useExternalWallet()

    // State
    const liff = ref(null)
    const config = ref(null)
    const allowNavFooter = ref(null)
    const contactCustomerLink = ref(null)
    const navItemWarningDialog = ref(false)
    const updateWarningDialog = ref(false)
    const showNavSettingDialog = ref(false)
    const functionMenuShowConfig = ref([])
    const functionMenu = reactive({
      wallet: true,
      coupon: false,
      point: false,
      address: false,
    })
    const formData = reactive({
      nav2: {
        type: '',
        name: '',
        link: { url: '' },
      },
      nav3: {
        type: '',
        name: '',
        link: { url: '' },
      },
      nav4: {
        type: '',
        name: '',
        link: { url: '' },
      },
      nav5: {
        type: '',
        name: '',
        link: { url: '' },
      },
    })

    // Computed
    const usePospal = computed(() => checkActionList(['adminView.memberCenterConfig.findPosPalMemberWallet', 'admin.externalWallet.page']))
    const useWallet = computed(() => checkAction('admin.walletRecord.page'))

    const navFooters = computed(() => {
      const footers = [{ type: 'memberCenter' }]
      const others = filter(formData, (nav) => nav !== '')
      forEach(others, (nav) => footers.push({ type: nav }))
      return footers
    })

    const useSalesRecordFeature = computed(() => checkAction('admin.salesRecord.page'))

    const useMemberQRCode = computed(() => checkAction('admin.memberCenterQRcode.page'))

    const useLiff = computed(() => checkAction('admin.lineLiff.page'))

    const allowNavFooterComputed = computed(() => {
      if (!useWallet.value) {
        return filter(allowNavFooter.value, (i) => i.type !== 'walletRecord')
      }
      if (!enableExternalWallet.value) {
        return filter(allowNavFooter.value, (i) => i.type !== 'posPalMemberWallet')
      }
      return allowNavFooter.value
    })

    // Methods
    const navTrans = (key) => {
      if (key === 'posPalMemberWallet') {
        return externalWalletName.value
      }
      return navConfig[key] ? navConfig[key].name : '尚未選擇'
    }

    const navUrl = (nav) => {
      if (!nav.link) return ''
      const linkType = nav.link.type
      if (linkType === 'lineId') return nav.link.lineId
      else if (linkType === 'email') return nav.link.email
      else if (linkType === 'phone') return nav.link.phone
      else if (linkType === 'url') return nav.link.url
      return nav.link.type
    }

    const getMemberCenterConfig = async () => {
      const [res, err] = await GetMemberCenterConfig({ shopId: shopId.value })
      if (err) {
        window.$message.error(err)
        return null
      }
      return res
    }

    const getShopInfo = async () => {
      const [res, err] = await GetShopInfo({ shopId: shopId.value })
      if (err) return window.$message.error(err)
      contactCustomerLink.value = res.contactCustomerLink
    }

    const getAllowFooterNav = async () => {
      const [res, err] = await GetAllowFooterNav({ shopId: shopId.value })
      if (err) return window.$message.error(err)
      allowNavFooter.value = res
    }

    const getLiff = async () => {
      try {
        const res = await GetLiff({ shopId: shopId.value })
        liff.value = res
      } catch (error) {
        liff.value = []
      }
    }

    const updateFunctionMenu = async () => {
      const [, err] = await UpdateMemberCenterConfig({
        shopId: shopId.value,
        functionMenuSetting: [
          { type: 'walletBalance', enable: functionMenu.wallet },
          { type: 'couponCount', enable: functionMenu.coupon },
          { type: 'bonusPointsBalance', enable: functionMenu.point },
          { type: 'userInfoMoreAddress', enable: functionMenu.address },
        ],
      })
      if (err) return window.$message.error(err)
      window.$message.success('更新成功！')
      await refresh()
    }

    const updateNavFooters = async (data) => {
      const [, err] = await UpdateMemberCenterConfig({
        shopId: shopId.value,
        footers: data,
      })
      if (err) return window.$message.error(err)
      window.$message.success('更新成功！')
      showNavSettingDialog.value = false
      await refresh()
    }

    const updateCardFunctionMenu = async (data) => {
      const { key, value } = data
      functionMenu[key] = value
      await updateFunctionMenu()
    }

    const hasTypeDuplicates = (data) => {
      return data.some((one, index) => {
        return data.findIndex((o) => o.type === one.type) !== index
      })
    }

    const refresh = async () => {
      await getShopInfo()
      const configRes = await getMemberCenterConfig()
      config.value = configRes

      await getAllowFooterNav()
      getLiff()

      // 同步功能快捷欄資料
      const syncNavType = (index) => {
        if (configRes && configRes.footers && configRes.footers[index]) {
          return {
            type: configRes.footers[index].type || '',
            name: configRes.footers[index].name || '',
            link: configRes.footers[index].link || {},
          }
        }
        return {
          type: '',
          name: '',
          link: { url: '', type: '' },
        }
      }

      formData.nav2 = syncNavType(1)
      formData.nav3 = syncNavType(2)
      formData.nav4 = syncNavType(3)
      formData.nav5 = syncNavType(4)

      // 同步功能菜單資料
      if (!configRes || !configRes.functionMenuSetting) {
        // 如果沒資料 自動打一次更新當預設資料
        await updateFunctionMenu()
        return
      }

      functionMenuShowConfig.value = configRes.functionMenuSetting

      const getFunctionMenuConfig = (type) => {
        const item = find(configRes.functionMenuSetting, { type: type })
        return item ? item.enable : false
      }

      functionMenu.wallet = getFunctionMenuConfig('walletBalance')
      functionMenu.coupon = getFunctionMenuConfig('couponCount')
      functionMenu.point = getFunctionMenuConfig('bonusPointsBalance')
      functionMenu.address = getFunctionMenuConfig('userInfoMoreAddress')
    }

    onMounted(async () => {
      getExternalWalletConfig()
      await refresh()
    })

    return {
      // State
      liff,
      config,
      allowNavFooter,
      contactCustomerLink,
      navItemWarningDialog,
      updateWarningDialog,
      showNavSettingDialog,
      functionMenuShowConfig,
      functionMenu,
      formData,

      // Computed
      navFooters,
      useSalesRecordFeature,
      useMemberQRCode,
      useLiff,
      allowNavFooterComputed,
      usePospal,
      useWallet,
      enableExternalWallet,
      externalWalletName,

      // Methods
      navTrans,
      navUrl,
      refresh,
      hasTypeDuplicates,
      updateNavFooters,
      updateFunctionMenu,
      updateCardFunctionMenu,
      getLiff,
    }
  },
})
</script>

<style lang="postcss" scoped>
.area-wrapper {
  @apply bg-white px-[30px] py-[24px];
}

.container-title {
  @apply font-medium text-[24px] leading-[34.75px];
}

.container-hint {
  @apply text-gray-20;
}

.gray-text {
  @apply text-gray-20;
}
</style>
